import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import Navigation from "@/components/navigation";
import StarfieldBackground from "@/components/starfield-background";
import { API_BASE_URL } from "@/lib/api";
export default function PoolPlayers() {
    const [tournaments, setTournaments] = useState([]);
    const [events, setEvents] = useState([]);
    const [phases, setPhases] = useState([]);
    const [pools, setPools] = useState([]);
    const [players, setPlayers] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('');
    const [selectedEvent, setSelectedEvent] = useState('');
    const [selectedPhase, setSelectedPhase] = useState('');
    const [selectedPool, setSelectedPool] = useState('');
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingPhases, setLoadingPhases] = useState(false);
    const [loadingPools, setLoadingPools] = useState(false);
    const [loadingPlayers, setLoadingPlayers] = useState(false);
    const [error, setError] = useState(null);
    const poolTitle = [
        events.find(e => e.id === selectedEvent)?.name,
        phases.find(p => p.id === selectedPhase)?.name,
        pools.find(p => p.id === selectedPool)?.displayIdentifier,
    ].filter(Boolean).join(', ');
    useEffect(() => {
        fetch(`${API_BASE_URL}/Results/tournaments/1`)
            .then(res => res.json())
            .then(data => setTournaments(data.message ?? []))
            .catch(() => setError('Failed to load tournaments.'));
    }, []);
    function handleTournamentChange(e) {
        const id = e.target.value;
        setSelectedTournament(id);
        setSelectedEvent('');
        setSelectedPhase('');
        setSelectedPool('');
        setEvents([]);
        setPhases([]);
        setPools([]);
        setPlayers([]);
        setError(null);
        if (!id)
            return;
        setLoadingEvents(true);
        fetch(`${API_BASE_URL}/Admin/events/${id}`)
            .then(res => res.json())
            .then(data => setEvents(data.message ?? []))
            .catch(() => setError('Failed to load events.'))
            .finally(() => setLoadingEvents(false));
    }
    function handleEventChange(e) {
        const id = Number(e.target.value);
        setSelectedEvent(id || '');
        setSelectedPhase('');
        setSelectedPool('');
        setPhases([]);
        setPools([]);
        setPlayers([]);
        setError(null);
        if (!id)
            return;
        setLoadingPhases(true);
        fetch(`${API_BASE_URL}/Admin/phases/${id}`)
            .then(res => res.json())
            .then(data => setPhases(data.message ?? []))
            .catch(() => setError('Failed to load phases.'))
            .finally(() => setLoadingPhases(false));
    }
    function handlePhaseChange(e) {
        const id = Number(e.target.value);
        setSelectedPhase(id || '');
        setSelectedPool('');
        setPools([]);
        setPlayers([]);
        setError(null);
        if (!id)
            return;
        setLoadingPools(true);
        fetch(`${API_BASE_URL}/Admin/pools/${id}`)
            .then(res => res.json())
            .then(data => setPools(data.message ?? []))
            .catch(() => setError('Failed to load pools.'))
            .finally(() => setLoadingPools(false));
    }
    function handlePoolChange(e) {
        const id = Number(e.target.value);
        setSelectedPool(id || '');
        setPlayers([]);
        setError(null);
        if (!id)
            return;
        setLoadingPlayers(true);
        fetch(`${API_BASE_URL}/Admin/pool-players/${id}`)
            .then(res => res.json())
            .then(data => setPlayers(data.message ?? []))
            .catch(() => setError('Failed to load pool players.'))
            .finally(() => setLoadingPlayers(false));
    }
    return (_jsxs("div", { className: "min-h-screen bg-space-dark text-white relative overflow-hidden", children: [_jsx(StarfieldBackground, {}), _jsx(Navigation, {}), _jsx("main", { className: "pt-24 pb-16 px-4 relative z-10", children: _jsxs("div", { className: "max-w-4xl mx-auto", children: [_jsx("h1", { className: "text-3xl font-orbitron font-bold text-txs-red mb-2", children: "Pool Players" }), _jsx("p", { className: "text-gray-400 mb-8", children: "Use the dropdowns below to view the players in a particular pool." }), error && (_jsx("div", { className: "mb-4 p-3 bg-red-900/40 border border-red-500/50 rounded-lg text-red-300 text-sm", children: error })), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-8", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-2", children: "1. Select a Tournament" }), _jsxs("select", { value: selectedTournament, onChange: handleTournamentChange, className: "w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red", children: [_jsx("option", { value: "", className: "bg-space-dark", children: "-- Select Tournament --" }), tournaments.map(t => (_jsx("option", { value: t.id, className: "bg-space-dark", children: t.name }, t.id)))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-2", children: "2. Select an Event" }), _jsxs("select", { value: selectedEvent, onChange: handleEventChange, disabled: !selectedTournament || loadingEvents, className: "w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red disabled:opacity-40", children: [_jsx("option", { value: "", className: "bg-space-dark", children: loadingEvents ? 'Loading...' : '-- Select Event --' }), events.map(ev => (_jsx("option", { value: ev.id, className: "bg-space-dark", children: ev.name }, ev.id)))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-2", children: "3. Select a Phase" }), _jsxs("select", { value: selectedPhase, onChange: handlePhaseChange, disabled: !selectedEvent || loadingPhases, className: "w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red disabled:opacity-40", children: [_jsx("option", { value: "", className: "bg-space-dark", children: loadingPhases ? 'Loading...' : '-- Select Phase --' }), phases.map(ph => (_jsx("option", { value: ph.id, className: "bg-space-dark", children: ph.name }, ph.id)))] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-semibold text-gray-300 mb-2", children: "4. Select a Pool" }), _jsxs("select", { value: selectedPool, onChange: handlePoolChange, disabled: !selectedPhase || loadingPools, className: "w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red disabled:opacity-40", children: [_jsx("option", { value: "", className: "bg-space-dark", children: loadingPools ? 'Loading...' : '-- Select Pool --' }), pools.map(pool => (_jsx("option", { value: pool.id, className: "bg-space-dark", children: pool.displayIdentifier }, pool.id)))] })] })] }), poolTitle && (_jsx("h2", { className: "text-xl font-orbitron font-bold text-txs-red mb-4", children: poolTitle })), loadingPlayers && (_jsx("p", { className: "text-gray-400", children: "Loading players..." })), !loadingPlayers && players.length > 0 && (_jsx("div", { className: "space-y-2", children: players.map((p, i) => (_jsx("div", { className: "px-4 py-3 bg-white/5 border border-white/10 rounded-lg font-semibold", children: p.playerId && selectedEvent ? (_jsx(Link, { href: `/admin/player-history?eventId=${selectedEvent}&playerId=${p.playerId}`, className: "text-white hover:text-txs-red transition-colors", target: "_blank", children: p.gamertag })) : (_jsx("span", { className: "text-white", children: p.gamertag })) }, i))) })), !loadingPlayers && selectedPool && players.length === 0 && (_jsx("p", { className: "text-gray-400", children: "No players found for this pool." }))] }) })] }));
}
